import React, { useEffect } from "react";
import ControlledForm from "~/shared/form/ControlledForm";
import TextInput from "~/shared/form/TextInput";
import { useForm, useFieldArray } from 'react-hook-form';
import SelectInput from "~/shared/form/SelectInput";
import { HOUSEHOLD_RELATIONSHIPS, PAYMENT_FREQUENCIES } from "./form-constants";
import Page from "~/shared/Page";
import FormSection from "~/shared/form/FormSection";
import FieldContext from "~/shared/form/FieldContext";
import FloatingPanel from "~/shared/FloatingPanel";
import axios from "axios";
import { setUserAccountStatusOverride } from "~/shared/auth-utils";
import FormResponse from "~/shared/form/FormResponse";
import { FullNameFields } from "./FieldGroups";
import FormButton from "~/shared/form/FormButton";

export default function HouseholdSetup() {
    const formHook = useForm({ defaultValues });
    const { watch, getValues } = formHook;
    const { fields, append, remove, move } = useFieldArray({ control: formHook.control, name: "members" });

    useEffect(() => {
        setTimeout(() => {
            append();
            remove(getValues("members").length - 1);
        }, 200);
    }, []);

    async function defaultValues() {
        const response = await axios.post("family/load-profile");
        const { parent: { firstName, middleName, lastName } } = response.data;
        return { members: [{ firstName, middleName, lastName, relationship: "Self", monthlyIncome: "" }] };
    }

    function noMonthlyIncome(index) {
        const monthlyIncome = watch(`members.${index}.monthlyIncome`);
        return !monthlyIncome || monthlyIncome === "0";
    }

    function onSubmit(data) {
        data.members = data.members.filter(m => !m.removed);
    }

    if (fields.length === 0) {
        append({ monthlyIncome: "" });
    }

    function getDisplayIndex(i) {
        let displayIndex = 0;

        for (let index = 0; index < i; index++) {
            if (!fields[index].removed) {
                displayIndex++;
            }
        }

        return displayIndex;
    }

    return (
        <Page title="Family Setup" navBar={[{ text: "Back", link: "/setup/parent" }]}>
            <div className="page-container">
                <FloatingPanel title="Household Information">
                    <ControlledForm hook={formHook} post="family/setup-household" redirect="/photo-video-form" onSubmit={onSubmit} onSuccess={() => setUserAccountStatusOverride("Photo Video Form Pending")}>
                        <p className="mb-4">
                            Fill out your household information below.
                            Required fields are marked with a star (*).
                        </p>
                        <div className="flex flex-col gap-3">
                            <FormSection row wrap maxWidth={17.5} gap={1}>
                                <TextInput label="Household Size" id="size" preset="integer" min={1} max={99} required />
                                <TextInput label="Family Unit Size" id="familyUnitSize" preset="integer" min={1} max={99} required />
                            </FormSection>
                            <span className="block text-neutral-600 text-sm text-left">
                                The Family Unit includes:
                                <ul>
                                    <li>A biological or adoptive parent of the dependent child for whom assistance is requested.</li>
                                    <li>An eligible minor sibling, (whole, half or adoptive) of the dependent child for whom assistance is requested.</li>
                                    <li>Other children living in the home who are within the specified degree of relationship to the grantee relative but who are not members of the Family Unit.</li>
                                    <li>A non-parent relative who is the caretaker if there is no parent in the home or if the only parent in the home receives SSI.</li>
                                </ul>
                            </span>
                            <FormSection row wrap maxWidth={30} gap={1}>
                                <TextInput label="Household Monthly Income" id="grossMonthlyIncome" preset="money" allowMinus={false} required>
                                    <span className="text-neutral-600 text-sm text-left mt-1">Enter income before taxes and deductions.</span>
                                </TextInput>
                                <TextInput label="Household Yearly Income" id="grossYearlyIncome" preset="money" allowMinus={false} required>
                                    <span className="text-neutral-600 text-sm text-left mt-1">Enter income before taxes and deductions.</span>
                                </TextInput>
                            </FormSection>
                            <p className="text-center my-2">
                                Enter information about yourself, the child(ren) who live with you, and the other parent of the child(ren) if he or she lives with you. Click the add member button to add a new member.
                            </p>
                            {fields.map((field, index) => !field.removed && <div key={field.id} className="border rounded shadow-sm p-3">
                                <h3 className="text-xl">Household Member #{getDisplayIndex(index) + 1}</h3>
                                <FieldContext id={`members.${index}`}>
                                    <div className="flex flex-col gap-3">
                                        <FormSection row wrap gap={1}>
                                            <FullNameFields frozen={index === 0} />
                                        </FormSection>
                                        <FormSection row wrap gap={1}>
                                            <SelectInput label="Relationship" id="relationship" minWidth={8} placeholder="Select relationship" options={index === 0 ? ["Self"] : HOUSEHOLD_RELATIONSHIPS} frozen={index === 0} required />
                                            <TextInput label="Date of Birth" id="dateOfBirth" preset="birthDate" required />
                                        </FormSection>
                                        <FormSection row wrap gap={1}>
                                            <TextInput label="Monthly Income" id="monthlyIncome" preset="money" allowMinus={false} deps={[`members.${index}.incomeSource`, `members.${index}.oftenReceived`]} required>
                                                <span className="text-neutral-600 text-sm text-left mt-1">Enter income before taxes and deductions. Enter $0 for no income.</span>
                                            </TextInput>
                                            <TextInput label="Income Source" id="incomeSource" disabled={noMonthlyIncome(index)} required />
                                            <SelectInput label="How Often Received" id="oftenReceived" disabled={noMonthlyIncome(index)} options={PAYMENT_FREQUENCIES} placeholder="Select frequency" errorLabel="frequency" required />
                                        </FormSection>
                                    </div>
                                </FieldContext>
                                {index > 0 && <FormButton type="button" className="mx-auto text-lg mt-3" onClick={() => (formHook.setValue(`members.${index}.removed`, true), move(0, 0))}>Remove</FormButton>}
                            </div>)}
                        </div>
                        <br />
                        <FormResponse />
                        <FormButton className="mx-auto" family onClick={() => append({ firstName: "", middleName: "", lastName: "", monthlyIncome: "" })}>Add Member</FormButton>
                        <FormButton className="mx-auto mt-6" family submit />
                    </ControlledForm>
                </FloatingPanel>
            </div>
        </Page>
    );
}
